import { useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import ProductTypes from './components/ProductTypes';
import Technology from './components/Technology';
import Portfolio from './components/Portfolio';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Blog from './components/Blog';
import Contact from './components/Contact';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.bioetiket.com';

    // Schema.org: LocalBusiness
    const localBizSchemaId = 'schema-home-localbusiness';
    let existingLB = document.getElementById(localBizSchemaId);
    if (existingLB) existingLB.remove();
    const lbScript = document.createElement('script');
    lbScript.id = localBizSchemaId;
    lbScript.type = 'application/ld+json';
    lbScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#localbusiness`,
      name: 'Bioetiket',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      image: `${siteUrl}/og-image.jpg`,
      description:
        'Bioetiket; rulo etiket, damla etiket, lazer etiket, gıda ambalaj etiketi ve özel baskı çözümleri sunan İstanbul Ümraniye merkezli profesyonel etiket üreticisidir.',
      telephone: '+90-216-365-88-78',
      email: 'info@bioetiket.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Yukarı Dudullu Mah. Necip Fazıl Blv. No: 70',
        addressLocality: 'Ümraniye',
        addressRegion: 'İstanbul',
        postalCode: '34775',
        addressCountry: 'TR',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 41.0167,
        longitude: 29.1167,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:30',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday'],
          opens: '09:00',
          closes: '14:00',
        },
      ],
      priceRange: '₺₺',
      currenciesAccepted: 'TRY',
      paymentAccepted: 'Nakit, Kredi Kartı, Banka Transferi',
      areaServed: [
        { '@type': 'City', name: 'Ümraniye' },
        { '@type': 'City', name: 'İstanbul' },
        { '@type': 'Country', name: 'Türkiye' },
      ],
      sameAs: [
        'https://www.instagram.com/bioetiket',
        'https://www.linkedin.com/company/bioetiket',
      ],
      hasMap: 'https://maps.google.com/?q=Bioetiket+Ümraniye+İstanbul',
      knowsAbout: [
        'Rulo Etiket Baskısı',
        'Damla Etiket',
        'Lazer Etiket',
        'Gıda Ambalaj Etiketi',
        'Özel Etiket Üretimi',
        'Flekso Baskı',
        'Dijital Baskı',
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '87',
        bestRating: '5',
        worstRating: '1',
      },
    });
    document.head.appendChild(lbScript);

    // Schema.org: FAQPage
    const faqSchemaId = 'schema-home-faq';
    let existing = document.getElementById(faqSchemaId);
    if (existing) existing.remove();
    const faqScript = document.createElement('script');
    faqScript.id = faqSchemaId;
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Minimum sipariş miktarı nedir?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dijital baskıda 100 adetten, flekso baskıda 10.000 adetten başlayan siparişler kabul edilmektedir.',
          },
        },
        {
          '@type': 'Question',
          name: 'Teslimat süresi ne kadar?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dijital baskıda 24-48 saat, flekso baskıda 3-5 iş günü içinde teslimat yapılmaktadır.',
          },
        },
        {
          '@type': 'Question',
          name: 'Ücretsiz numune alabilir miyim?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Evet, tüm ürün gruplarımız için ücretsiz numune talep edebilirsiniz.',
          },
        },
      ],
    });
    document.head.appendChild(faqScript);

    // Schema.org: WebSite + SearchAction
    const websiteSchemaId = 'schema-home-website';
    let existingWS = document.getElementById(websiteSchemaId);
    if (existingWS) existingWS.remove();
    const wsScript = document.createElement('script');
    wsScript.id = websiteSchemaId;
    wsScript.type = 'application/ld+json';
    wsScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Bioetiket',
      url: siteUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/blog?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    });
    document.head.appendChild(wsScript);

    // Schema.org: Organization
    const orgSchemaId = 'schema-home-organization';
    let existingOrg = document.getElementById(orgSchemaId);
    if (existingOrg) existingOrg.remove();
    const orgScript = document.createElement('script');
    orgScript.id = orgSchemaId;
    orgScript.type = 'application/ld+json';
    orgScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Bioetiket',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/og-image.jpg`,
        width: 1200,
        height: 630,
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+90-216-365-88-78',
        contactType: 'customer service',
        areaServed: 'TR',
        availableLanguage: 'Turkish',
      },
      sameAs: [
        'https://www.instagram.com/bioetiket',
        'https://www.linkedin.com/company/bioetiket',
        'https://www.facebook.com/bioetiket',
      ],
    });
    document.head.appendChild(orgScript);

    // Schema.org: BreadcrumbList (homepage)
    const bcHomeId = 'schema-home-breadcrumb';
    let existingBcHome = document.getElementById(bcHomeId);
    if (existingBcHome) existingBcHome.remove();
    const bcHomeScript = document.createElement('script');
    bcHomeScript.id = bcHomeId;
    bcHomeScript.type = 'application/ld+json';
    bcHomeScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${siteUrl}/` },
      ],
    });
    document.head.appendChild(bcHomeScript);

    // Schema.org: Service
    const serviceSchemaId = 'schema-home-service';
    let existingSvc = document.getElementById(serviceSchemaId);
    if (existingSvc) existingSvc.remove();
    const svcScript = document.createElement('script');
    svcScript.id = serviceSchemaId;
    svcScript.type = 'application/ld+json';
    svcScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Etiket Baskı ve Üretim',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Bioetiket',
        url: siteUrl,
      },
      areaServed: {
        '@type': 'City',
        name: 'İstanbul',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Etiket Ürün ve Hizmetleri',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Rulo Etiket Baskısı' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Damla Etiket Üretimi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lazer Etiket' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Food Packaging Etiketi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom Coffee Bags' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Paslanmaz Etiket' } },
        ],
      },
    });
    document.head.appendChild(svcScript);

    return () => {
      const s0 = document.getElementById(localBizSchemaId);
      if (s0) s0.remove();
      const s1 = document.getElementById(faqSchemaId);
      if (s1) s1.remove();
      const s2 = document.getElementById(serviceSchemaId);
      if (s2) s2.remove();
      const s3 = document.getElementById(websiteSchemaId);
      if (s3) s3.remove();
      const s4 = document.getElementById(orgSchemaId);
      if (s4) s4.remove();
      const s5 = document.getElementById(bcHomeId);
      if (s5) s5.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Hero />
      <Services />
      <ProductTypes />
      <Technology />
      <Portfolio />
      <Testimonials />
      <FAQ />
      <Blog />
      <Contact />
      <WhatsAppButton />
      <Footer />
    </div>
  );
}
